import { AUTHORITIES } from '@/config/constants';
import { useAppDispatch, useAppSelector } from '@/config/store';
import { hasAnyAuthority } from '@/shared/auth/private-route';
import { AuthenticationState } from '@/shared/reducers/authentication';
import { EdoParentState, getParentChildren } from '@/shared/reducers/entities/edo-parent.reducer';
import { EdoStudentState, getStudentClasses } from '@/shared/reducers/entities/edo-student.reducer';
import { EdoTeacherState, getTeacherClasses } from '@/shared/reducers/entities/edo-teacher.reducer';
import { cn } from '@/shared/util/cn';
import isSelected from '@/shared/util/isSelected';
import { useMediaQuery } from '@/shared/util/useMediaQuery';
import {
  BarChart3,
  BookMarked,
  BookOpen,
  ClipboardList,
  Megaphone,
  Newspaper,
  School,
  Shield,
  UserCircle,
  Video
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export const DRAWER_WIDTH = 256;

interface IDrawerProps {
  window?: () => Window;
  mobileOpen: boolean;
  onDrawerToggle: () => void;
}

const navLinkBase =
  'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 border border-transparent';

function NavLink({
  to,
  onClick,
  isActive,
  icon: Icon,
  children,
  secondary,
  accent,
}: {
  to: string;
  onClick: () => void;
  isActive: boolean;
  icon: React.ElementType;
  children: React.ReactNode;
  secondary?: string;
  accent?: string;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const style = useMemo(() => {
    if (accent) {
      if (isActive) {
        return {
          background: `${accent}18`,
          color: accent,
          borderColor: `${accent}33`,
        };
      }
      if (isHovered) {
        return {
          background: `${accent}0D`,
          color: accent,
          borderColor: 'transparent',
        };
      }
    }
    return undefined;
  }, [isActive, isHovered, accent]);

  return (
    <Link
      to={to}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        navLinkBase,
        !isActive && !accent && 'text-[var(--color-mockup-text-mid)] hover:bg-primary/10 hover:text-primary dark:text-[var(--color-mockup-text-mid)] dark:hover:text-primary',
        !isActive && accent && 'text-[var(--color-mockup-text-mid)] dark:text-[var(--color-mockup-text-mid)]',
        isActive && !accent && 'bg-primary/15 text-primary border-primary/25 dark:bg-primary/20 dark:border-primary/30',
      )}
      style={style}
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden />
      <span className="flex flex-col min-w-0">
        <span>{children}</span>
        {secondary && (
          <span className="text-xs text-[var(--color-mockup-text-light)] truncate">{secondary}</span>
        )}
      </span>
      {isActive && (
        <div
          className={cn('ml-auto w-2 h-2 rounded-full flex-shrink-0', !accent && 'bg-primary')}
          style={accent ? { background: accent } : undefined}
          aria-hidden
        />
      )}
    </Link>
  );
}

const Drawer: React.FC<IDrawerProps> = (props) => {
  const { mobileOpen, onDrawerToggle: handleDrawerToggle } = props;
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const isMobile = !isDesktop;

  const { account } = useAppSelector<AuthenticationState>((state) => state.authentication);
  const { children } = useAppSelector<EdoParentState>((state) => state.edoParent);
  const { classes: teacherClasses } = useAppSelector<EdoTeacherState>((state) => state.edoTeacher);
  const { classes: studentClasses } = useAppSelector<EdoStudentState>((state) => state.edoStudent);

  useEffect(() => {
    if (!account.id) return;
    if (hasAnyAuthority(account.roles, [AUTHORITIES.ROLE_TEACHER])) {
      dispatch(
        getTeacherClasses({
          id: account.id,
          page: 0,
          size: 1000,
          sort: ['year.year,ASC', 'section,ASC'],
        })
      );
    } else if (hasAnyAuthority(account.roles, [AUTHORITIES.ROLE_STUDENT])) {
      dispatch(
        getStudentClasses({
          id: account.id,
          page: 0,
          size: 1000,
          sort: ['year.year,ASC', 'section,ASC'],
        })
      );
    } else if (hasAnyAuthority(account.roles, [AUTHORITIES.ROLE_PARENT])) {
      dispatch(getParentChildren(account.id));
    }
  }, [account.id, dispatch]);

  const classes = useMemo(() => {
    if (hasAnyAuthority(account.roles, [AUTHORITIES.ROLE_TEACHER])) return teacherClasses;
    if (hasAnyAuthority(account.roles, [AUTHORITIES.ROLE_STUDENT])) return studentClasses;
    return [];
  }, [account.roles, teacherClasses, studentClasses]);

  const drawerContent = (
    <>
      <div className="flex flex-col py-6 flex-1 min-h-0">
        <div className="px-6 mb-8">
          <Link
            to={hasAnyAuthority(account.roles, [AUTHORITIES.ROLE_PARENT]) ? '/parents' : '/'}
            onClick={handleDrawerToggle}
            className="flex items-center gap-2.5 no-underline"
          >
            <div
              className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center text-lg font-black text-white flex-shrink-0"
              style={{
                boxShadow: '0 0px 9px rgba(18, 181, 170, 0.35)',
              }}
            >
              <img src="/package/logo.png" alt="eDocendo" className="h-10 w-10" />
            </div>
            <div className="flex flex-col">
            <span className="text-xl font-black font-heading text-[var(--color-mockup-text)]">
              eDocendo
            </span>
            <span className="text-xs font-semibold text-primary">
              Istituto Giovanni Paolo II
              </span>
            </div>
          </Link>
        </div>
        <div className="flex-1 px-3 space-y-0.5 overflow-y-auto min-h-0">
      {hasAnyAuthority(account.roles, [
        AUTHORITIES.ROLE_ADMIN,
        AUTHORITIES.ROLE_VICE,
        AUTHORITIES.ROLE_TEACHER,
        AUTHORITIES.ROLE_STUDENT,
        AUTHORITIES.ROLE_SUPPORT_TEACHER,
      ]) && (
        <>
          <nav className="space-y-0.5">
            {[
              {
                text: 'Feed',
                icon: Newspaper,
                href: '/',
                roles: [
                  AUTHORITIES.ROLE_ADMIN,
                  AUTHORITIES.ROLE_VICE,
                  AUTHORITIES.ROLE_TEACHER,
                  AUTHORITIES.ROLE_STUDENT,
                  AUTHORITIES.ROLE_SUPPORT_TEACHER,
                ],
                selectPattern: '/',
                accent: '#12b5aa',
              },
              {
                text: 'Il mio archivio',
                icon: BookMarked,
                href: '/repository',
                roles: [AUTHORITIES.ROLE_ADMIN, AUTHORITIES.ROLE_VICE, AUTHORITIES.ROLE_TEACHER],
                selectPattern: '/repository/*',
                accent: '#7c5cfc',
              },
              {
                text: 'I miei test',
                icon: ClipboardList,
                href: '/quizzes',
                roles: [AUTHORITIES.ROLE_TEACHER],
                selectPattern: '/quizzes/*',
                accent: '#3ab0f5',
              },
            ].map(
              (item) =>
                hasAnyAuthority(account.roles, item.roles) && (
                  <NavLink
                    key={item.text}
                    to={item.href}
                    onClick={handleDrawerToggle}
                    isActive={isSelected(location, item.selectPattern)}
                    icon={item.icon}
                    accent={item.accent}
                  >
                    {item.text}
                  </NavLink>
                )
            )}
          </nav>
        </>
      )}
      {hasAnyAuthority(account.roles, [
        AUTHORITIES.ROLE_ADMIN,
        AUTHORITIES.ROLE_SUPPORT_TEACHER,
        AUTHORITIES.ROLE_TEACHER,
        AUTHORITIES.ROLE_VICE,
        AUTHORITIES.ROLE_STUDENT,
      ]) && (
        <>
          <nav className="space-y-0.5">
            {[
              {
                text: 'Le mie classi',
                icon: School,
                href: '/classes',
                roles: [AUTHORITIES.ROLE_VICE, AUTHORITIES.ROLE_TEACHER],
                selectPattern: '/classes',
                accent: '#f5a623',
              },
              {
                text: 'Dispense',
                icon: BookOpen,
                href: '/wiki',
                roles: [
                  AUTHORITIES.ROLE_ADMIN,
                  AUTHORITIES.ROLE_SUPPORT_TEACHER,
                  AUTHORITIES.ROLE_TEACHER,
                  AUTHORITIES.ROLE_VICE,
                  AUTHORITIES.ROLE_STUDENT,
                  AUTHORITIES.ROLE_PARENT,
                ],
                selectPattern: '/wiki/*',
                accent: '#ff6b5b',
              },
              {
                text: 'Videoteca',
                icon: Video,
                href: '/videostore',
                roles: [
                  AUTHORITIES.ROLE_ADMIN,
                  AUTHORITIES.ROLE_SUPPORT_TEACHER,
                  AUTHORITIES.ROLE_TEACHER,
                  AUTHORITIES.ROLE_VICE,
                  AUTHORITIES.ROLE_STUDENT,
                  AUTHORITIES.ROLE_PARENT,
                ],
                selectPattern: '/videostore/*',
                accent: '#f5a623',
              },
              {
                text: 'Admin',
                icon: Shield,
                href: '/admin',
                roles: [AUTHORITIES.ROLE_ADMIN],
                selectPattern: '/admin/*',
                accent: '#3ab0f5',
              },
            ].map(
              (item) =>
                hasAnyAuthority(account.roles, item.roles) && (
                  <NavLink
                    key={item.text}
                    to={item.href}
                    onClick={handleDrawerToggle}
                    isActive={isSelected(location, item.selectPattern)}
                    icon={item.icon}
                    accent={item.accent}
                  >
                    {item.text}
                  </NavLink>
                )
            )}
          </nav>
        </>
      )}
      {hasAnyAuthority(account.roles, [AUTHORITIES.ROLE_ADMIN, AUTHORITIES.ROLE_VICE]) && (
        <>
          <div className="pt-5 pb-2 px-4">
            <span className="text-xs font-black tracking-[0.1em] text-[var(--color-mockup-text-light)]">
              AMMINISTRAZIONE
            </span>
          </div>
          <nav className="space-y-0.5 px-3">
            {[
              {
                text: 'Gestione circolari',
                icon: Megaphone,
                href: '/circulars-management',
                roles: [AUTHORITIES.ROLE_ADMIN, AUTHORITIES.ROLE_VICE],
                selectPattern: '/circulars-management/*',
                accent: '#ff6b5b',
              },
              {
                text: 'Controllo attività docenti',
                icon: BarChart3,
                href: '/teachers-supervision',
                roles: [AUTHORITIES.ROLE_ADMIN, AUTHORITIES.ROLE_VICE],
                selectPattern: '/teachers-supervision/*',
                accent: '#7c5cfc',
              },
            ].map(
              (item) =>
                hasAnyAuthority(account.roles, item.roles) && (
                  <NavLink
                    key={item.text}
                    to={item.href}
                    onClick={handleDrawerToggle}
                    isActive={isSelected(location, item.selectPattern)}
                    icon={item.icon}
                    accent={item.accent}
                  >
                    {item.text}
                  </NavLink>
                )
            )}
          </nav>
        </>
      )}
      {hasAnyAuthority(account.roles, [AUTHORITIES.ROLE_PARENT]) && (
        <>
          <div className="pt-5 pb-2 px-4">
            <span className="text-xs font-black tracking-[0.1em] text-[var(--color-mockup-text-light)]">
              FIGLI
            </span>
          </div>
          <nav className="space-y-0.5 px-3">
            {children?.entities?.map((child, i) => (
              <NavLink
                key={child.userId}
                to={`/parents/children/${child.userId}`}
                onClick={handleDrawerToggle}
                isActive={isSelected(location, `/parents/children/${child.userId}/*`)}
                icon={UserCircle}
                accent={['#12b5aa', '#7c5cfc', '#ff6b5b', '#f5a623', '#3ab0f5'][i % 5]}
              >
                {child.user.firstName} {child.user.lastName}
              </NavLink>
            ))}
          </nav>
        </>
      )}
      {hasAnyAuthority(account.roles, [AUTHORITIES.ROLE_TEACHER, AUTHORITIES.ROLE_STUDENT]) &&
        classes.length > 0 && (
          <>
            <div className="pt-5 pb-2 px-4">
              <span className="text-xs font-black tracking-[0.1em] text-[var(--color-mockup-text-light)]">
                LE MIE CLASSI
              </span>
            </div>
            <nav className="space-y-0.5 px-3">
              {classes.map((edoClass, i) => (
                <NavLink
                  key={edoClass.id}
                  to={`/classes/${edoClass.id}`}
                  onClick={handleDrawerToggle}
                  isActive={isSelected(location, `/classes/${edoClass.id}`)}
                  icon={School}
                  secondary={edoClass.school?.code}
                  accent={['#f5a623', '#7c5cfc', '#12b5aa', '#ff6b5b', '#3ab0f5'][i % 5]}
                >
                  {edoClass.year?.year}°{edoClass.section}
                </NavLink>
              ))}
            </nav>
          </>
        )}
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile: overlay + temporary drawer */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          aria-hidden
          onClick={handleDrawerToggle}
        />
      )}
      {isMobile && (
        <aside
          className={cn(
            'fixed left-0 top-0 z-50 h-full w-64 overflow-hidden flex flex-col border-r bg-surface transition-transform duration-200 ease-out',
            'border-[var(--color-mockup-border)]',
            mobileOpen ? 'translate-x-0' : '-translate-x-full'
          )}
          style={{ width: DRAWER_WIDTH }}
        >
          {drawerContent}
        </aside>
      )}
      {/* Desktop: permanent sidebar */}
      {isDesktop && (
        <aside
          className="fixed left-0 top-0 z-40 hidden h-full w-64 shrink-0 overflow-hidden flex flex-col border-r bg-surface border-[var(--color-mockup-border)] dark:border-[var(--color-mockup-border)] md:flex"
          style={{ width: DRAWER_WIDTH }}
        >
          {drawerContent}
        </aside>
      )}
    </>
  );
};

export default Drawer;
